import * as React from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from 'src/shadcn/components/ui/card';
import {
  RadioGroup,
  RadioGroupItem,
} from 'src/shadcn/components/ui/radio-group';
import { Label } from 'src/shadcn/components/ui/label';
import { cn } from 'src/shadcn/lib/utils';
import { Button } from 'src/shadcn/components/ui/button';
import { useAuthContext } from '../../AuthContext';
import { gql } from '@apollo/client';
import { useHotToast } from 'src/utils/useHotToast';
import {
  useLoginAsAdminLazyQuery,
  useLoginAsShopLazyQuery,
} from './__generated__/LoginStylePage.generated';
import { RotateCw } from 'lucide-react';
import { LoginAsShopInput } from 'src/graphql/__generated__/globalTypes';
import { AuthPageLeftSection } from './AuthPageLeftSection';
import { AuthPage } from './AuthPage';
import { AuthPageRightSection } from './AuthPageRightSection';
import { ShopLocationPicker } from 'src/Components/ShopLocationPicker/ShopLocationPicker';
import { GetShopLocationsForContextQuery } from '../__generated__/PageWithUserContext.generated';

export interface ILoginStylePageProps {}

export const LoginStylePage: React.FunctionComponent<
  ILoginStylePageProps
> = props => {
  const [loginType, setLoginType] = React.useState<
    'admin-login' | 'location-login'
  >('admin-login');
  const authContext = useAuthContext();
  const { loginAsAdmin, isLoggingAsAdmin } = useLoginAsAdmin();
  const { loginAsShop, isLoggingAsShop } = useLoginAsShop();
  const [isLocationSelectionOpen, setIsLocationSelectionOpen] =
    React.useState(false);
  const [selectedLocation, setSelectedLocation] =
    React.useState<
      GetShopLocationsForContextQuery['getShopLocations'][number]
    >();
  const toast = useHotToast();
  return (
    <AuthPage>
      <AuthPageLeftSection></AuthPageLeftSection>
      <AuthPageRightSection>
        <Card className='m-auto mt-10 gap-8 border-none shadow-none'>
          {!isLocationSelectionOpen && (
            <>
              <CardHeader>
                <CardTitle>Login type</CardTitle>
                <CardDescription>Select your login type</CardDescription>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={loginType}
                  className='grid grid-cols-1 gap-5'
                >
                  <div
                    className={cn(
                      'flex items-center space-x-2 rounded border p-5',
                      {
                        ['outline outline-2']: loginType === 'location-login',
                      }
                    )}
                    onClick={() => {
                      setLoginType('location-login');
                    }}
                  >
                    <RadioGroupItem
                      value='location-login'
                      id='login-location'
                    />
                    <Label htmlFor='login-location'>
                      <div className='ml-3 flex flex-col gap-3'>
                        <div className='font-bold'>Employee location login</div>
                        <div>
                          This will let your employees log into a selected
                          location.
                        </div>
                      </div>
                    </Label>
                  </div>

                  <div
                    className={cn(
                      'flex items-center space-x-2 rounded border p-5',
                      {
                        ['outline outline-2']: loginType === 'admin-login',
                      }
                    )}
                    onClick={() => {
                      setLoginType('admin-login');
                    }}
                  >
                    <RadioGroupItem value='admin-login' id='admin-login' />
                    <Label htmlFor='admin-login'>
                      <div className='ml-3 flex flex-col gap-3'>
                        <div className='font-bold'>Admin login</div>
                        <div>
                          When you log in using this type, you will have access
                          to all location information.
                        </div>
                      </div>
                    </Label>
                  </div>
                </RadioGroup>
              </CardContent>
              <CardFooter className='flex justify-end gap-3'>
                <Button variant={'ghost'} onClick={() => authContext.logout()}>
                  Log out
                </Button>
                <Button
                  role='button'
                  onClick={() => {
                    if (loginType === 'admin-login') {
                      loginAsAdmin();
                    } else if (loginType === 'location-login') {
                      setIsLocationSelectionOpen(true);
                    }
                  }}
                >
                  <span>Continue</span>
                  {isLoggingAsAdmin && (
                    <RotateCw className='ml-2 h-4 w-4 animate-spin' />
                  )}
                </Button>
              </CardFooter>
            </>
          )}
          {isLocationSelectionOpen && (
            <>
              <CardHeader>
                <CardTitle>Select location for employees to login</CardTitle>
              </CardHeader>
              <CardContent>
                <ShopLocationPicker
                  selectedLocation={selectedLocation}
                  onLocationPicked={newLocation => {
                    setSelectedLocation(newLocation);
                  }}
                />
              </CardContent>
              <CardFooter className='flex justify-end gap-3'>
                <Button
                  variant={'ghost'}
                  onClick={() => {
                    setIsLocationSelectionOpen(false);
                  }}
                >
                  <span>Back</span>
                </Button>

                <Button
                  role='button'
                  onClick={() => {
                    if (selectedLocation?.id) {
                      loginAsShop({
                        shopLocationId: selectedLocation.id,
                      });
                    } else {
                      toast.error('You must select a location');
                    }
                  }}
                >
                  <span>Continue</span>
                  {isLoggingAsShop && (
                    <RotateCw className='ml-2 h-4 w-4 animate-spin' />
                  )}
                </Button>
              </CardFooter>
            </>
          )}
        </Card>
      </AuthPageRightSection>
    </AuthPage>
  );
};

function useLoginAsAdmin() {
  const toast = useHotToast();
  const authContext = useAuthContext();
  const [loginAsAdmin, { loading: isLoggingAsAdmin }] =
    useLoginAsAdminLazyQuery({
      onError: e => {
        toast.error(e.message);
      },
      onCompleted: res => {
        authContext.onLogin(res.loginAsAdmin);
      },
    });

  return {
    loginAsAdmin,
    isLoggingAsAdmin,
  };
}

const LOGIN_AS_ADMIN = gql`
  query LoginAsAdmin {
    loginAsAdmin {
      user {
        id
        companyName
        authUserType
        firstName
        lastName
      }
      token
      authTokenType
      shopLocation {
        id
        name
      }
    }
  }
`;

function useLoginAsShop() {
  const toast = useHotToast();
  const authContext = useAuthContext();
  const [loginAsShop, { loading: isLoggingAsShop }] = useLoginAsShopLazyQuery({
    onError: e => {
      toast.error(e.message);
    },
    onCompleted: res => {
      authContext.onLogin(res.loginAsShop);
    },
  });

  return {
    loginAsShop: (input: LoginAsShopInput) => {
      loginAsShop({ variables: { input } });
    },
    isLoggingAsShop,
  };
}

const LOGIN_AS_SHOP = gql`
  query LoginAsShop($input: LoginAsShopInput!) {
    loginAsShop(input: $input) {
      user {
        id
        companyName
        authUserType
      }
      token
      authTokenType
      shopLocation {
        id
        name
      }
    }
  }
`;
